/**
 * Attorney Dashboard - Cases & Client Management
 * Attorneys can view released cases AND submit new clients
 * Uses AttorneyLayout for consistent look with main app
 * Includes search, filter, and new client submission
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Scale,
  FileText,
  DollarSign,
  Calendar,
  ChevronRight,
  Loader2,
  AlertCircle,
  RefreshCw,
  TrendingUp,
  Search,
  Filter,
  X,
  Clock,
  CheckCircle2,
  UserPlus,
  Users,
  Mail,
  Phone,
  Building2,
  CalendarDays,
  AlertTriangle,
  Bell
} from 'lucide-react';
import AttorneyRoleLayout from '../components/AttorneyRoleLayout';
import CasePortfolio from '../components/dashboard/CasePortfolio';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface ReleasedCase {
  id: string;
  client_name: string;
  business_name?: string;
  event_type?: string;
  total_demand?: number;
  released_at: string;
  status: string;
}

interface SubmittedClient {
  id: string;
  claimant_name: string;
  client_email: string;
  business_name?: string;
  event_type?: string;
  status: string;
  created_at: string;
}

interface DeadlineAlert {
  case_id: string;
  client_name: string;
  deadline_type: string;
  deadline_date: string;
  days_remaining: number;
  urgency: 'critical' | 'warning' | 'info';
}

interface QuickInsights {
  cases_ready_for_action: number;
  total_demand_value: number;
  cases_in_negotiation: number;
  average_case_age_days: number;
}

type SortOption = 'newest' | 'oldest' | 'highest' | 'lowest' | 'name';
type FilterOption = 'all' | 'fire' | 'water' | 'storm' | 'other';
type TabOption = 'cases' | 'clients';

export default function AttorneyDashboard() {
  const [cases, setCases] = useState<ReleasedCase[]>([]);
  const [submittedClients, setSubmittedClients] = useState<SubmittedClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lawFirmName, setLawFirmName] = useState('');
  const [activeTab, setActiveTab] = useState<TabOption>('cases');
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // New Client Modal state
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [newClientForm, setNewClientForm] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    business_name: '',
    business_type: '',
    event_type: '',
    event_date: '',
    notes: '',
    send_email: true
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState('');

  // Alerts & Insights state
  const [deadlineAlerts, setDeadlineAlerts] = useState<DeadlineAlert[]>([]);
  const [quickInsights, setQuickInsights] = useState<QuickInsights | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('attorney_token');
    if (!token) {
      navigate('/attorney/login');
      return;
    }
    fetchDashboard();
    fetchSubmittedClients();
    fetchAlerts();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('attorney_token');
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('attorney_token');
        localStorage.removeItem('attorney_profile');
        navigate('/attorney/login');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard');
      }

      const data = await response.json();
      setCases(data.cases || []);
      setLawFirmName(data.law_firm || '');
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmittedClients = async () => {
    try {
      const token = localStorage.getItem('attorney_token');
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSubmittedClients(data.clients || []);
      }
    } catch (err) {
      console.error('Failed to fetch submitted clients:', err);
    }
  };

  const fetchAlerts = async () => {
    try {
      const token = localStorage.getItem('attorney_token');
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney/dashboard/alerts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setDeadlineAlerts(data.deadline_alerts || []);
        setQuickInsights(data.quick_insights || null);
      }
    } catch (err) {
      // Silently fail - alerts are non-critical
      console.error('Failed to fetch dashboard alerts:', err);
    }
  };

  const getDeadlineLabel = (type: string): string => {
    switch (type) {
      case 'carrier_response': return 'Carrier Response Due';
      case 'mediation_window': return 'Mediation Window';
      default: return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    }
  };

  const getUrgencyStyles = (urgency: string) => {
    switch (urgency) {
      case 'critical':
        return {
          bg: 'bg-red-50 border-red-200',
          icon: 'text-red-600',
          badge: 'bg-red-100 text-red-700',
          text: 'text-red-800'
        };
      case 'warning':
        return {
          bg: 'bg-amber-50 border-amber-200',
          icon: 'text-amber-600',
          badge: 'bg-amber-100 text-amber-700',
          text: 'text-amber-800'
        };
      default:
        return {
          bg: 'bg-blue-50 border-blue-200',
          icon: 'text-blue-600',
          badge: 'bg-blue-100 text-blue-700',
          text: 'text-blue-800'
        };
    }
  };

  const handleSubmitNewClient = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSubmitSuccess('');

    try {
      const token = localStorage.getItem('attorney_token');
      const response = await fetch(`${BACKEND_URL}/api/v1/attorney/clients`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newClientForm)
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to submit client');
      }

      const data = await response.json();
      setSubmitSuccess(data.message);
      
      // Reset form
      setNewClientForm({
        client_name: '',
        client_email: '',
        client_phone: '',
        business_name: '',
        business_type: '',
        event_type: '',
        event_date: '',
        notes: '',
        send_email: true
      });
      
      // Refresh clients list
      fetchSubmittedClients();
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowNewClientModal(false);
        setSubmitSuccess('');
      }, 2000);
      
    } catch (err: any) {
      setError(err.message || 'Failed to submit client');
    } finally {
      setSubmitting(false);
    }
  };

  // Filtered and sorted cases
  const filteredCases = useMemo(() => {
    let result = [...cases];
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(c => 
        c.client_name?.toLowerCase().includes(query) ||
        c.business_name?.toLowerCase().includes(query) ||
        c.event_type?.toLowerCase().includes(query)
      );
    }
    
    // Event type filter
    if (filterBy !== 'all') {
      result = result.filter(c => {
        const eventType = c.event_type?.toLowerCase() || '';
        switch (filterBy) {
          case 'fire': return eventType.includes('fire');
          case 'water': return eventType.includes('water') || eventType.includes('flood');
          case 'storm': return eventType.includes('storm') || eventType.includes('hurricane') || eventType.includes('wind');
          case 'other': return !['fire', 'water', 'flood', 'storm', 'hurricane', 'wind'].some(t => eventType.includes(t));
          default: return true;
        }
      });
    }
    
    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.released_at).getTime() - new Date(a.released_at).getTime();
        case 'oldest':
          return new Date(a.released_at).getTime() - new Date(b.released_at).getTime();
        case 'highest':
          return (b.total_demand || 0) - (a.total_demand || 0);
        case 'lowest':
          return (a.total_demand || 0) - (b.total_demand || 0);
        case 'name':
          return (a.client_name || '').localeCompare(b.client_name || '');
        default:
          return 0;
      }
    });
    
    return result;
  }, [cases, searchQuery, sortBy, filterBy]);

  const formatCurrency = (amount?: number) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getRelativeTime = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  // Calculate summary stats
  const totalDemand = cases.reduce((sum, c) => sum + (c.total_demand || 0), 0);
  const avgDemand = cases.length > 0 ? totalDemand / cases.length : 0;

  return (
    <AttorneyRoleLayout>
      {/* Page Header with New Client Button */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Attorney Dashboard</h1>
          <p className="text-slate-600 mt-1">
            Manage clients and view finalized reports
          </p>
        </div>
        <button
          onClick={() => setShowNewClientModal(true)}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm font-medium"
          data-testid="new-client-btn"
        >
          <UserPlus className="w-5 h-5" />
          New Client
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">{cases.length}</p>
              <p className="text-sm text-slate-500">Released Cases</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">{submittedClients.length}</p>
              <p className="text-sm text-slate-500">Submitted Clients</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">{formatCurrency(totalDemand)}</p>
              <p className="text-sm text-slate-500">Total Demand</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-slate-900">{formatCurrency(avgDemand)}</p>
              <p className="text-sm text-slate-500">Avg. per Case</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Insights Bar */}
      {quickInsights && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
            <p className="text-xs text-blue-600 font-medium">Ready for Action</p>
            <p className="text-2xl font-bold text-blue-700">{quickInsights.cases_ready_for_action}</p>
          </div>
          <div className="bg-green-50 border border-green-100 rounded-lg px-4 py-3">
            <p className="text-xs text-green-600 font-medium">Total Demand Value</p>
            <p className="text-2xl font-bold text-green-700">{formatCurrency(quickInsights.total_demand_value)}</p>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-3">
            <p className="text-xs text-amber-600 font-medium">In Negotiation</p>
            <p className="text-2xl font-bold text-amber-700">{quickInsights.cases_in_negotiation}</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
            <p className="text-xs text-slate-500 font-medium">Avg Case Age</p>
            <p className="text-2xl font-bold text-slate-700">{quickInsights.average_case_age_days}d</p>
          </div>
        </div>
      )}

      {/* Deadline Alerts */}
      {deadlineAlerts.length > 0 && (
        <div className="mb-6 space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="w-4 h-4 text-slate-500" />
            <h3 className="text-sm font-semibold text-slate-700">Upcoming Deadlines</h3>
          </div>
          {deadlineAlerts.map((alert, idx) => {
            const styles = getUrgencyStyles(alert.urgency);
            return (
              <div
                key={`${alert.case_id}-${alert.deadline_type}-${idx}`}
                className={`flex items-center justify-between px-4 py-3 rounded-lg border cursor-pointer hover:shadow-sm transition-shadow ${styles.bg}`}
                onClick={() => navigate(`/attorney/case/${alert.case_id}`)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${styles.icon}`} />
                  <div className="min-w-0">
                    <p className={`text-sm font-medium truncate ${styles.text}`}>
                      {alert.client_name} — {getDeadlineLabel(alert.deadline_type)}
                    </p>
                    <p className="text-xs text-slate-500">
                      Due {formatDate(alert.deadline_date)}
                    </p>
                  </div>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${styles.badge}`}>
                  {alert.days_remaining}d left
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Tabs: Cases vs Submitted Clients */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab('cases')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'cases'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
          data-testid="tab-cases"
        >
          Released Cases ({cases.length})
        </button>
        <button
          onClick={() => setActiveTab('clients')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'clients'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
          data-testid="tab-clients"
        >
          Submitted Clients ({submittedClients.length})
        </button>
      </div>

      {/* Submitted Clients Tab */}
      {activeTab === 'clients' && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900">Clients You've Submitted</h3>
            <p className="text-sm text-slate-500">Track the status of your submitted clients</p>
          </div>
          
          {submittedClients.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No Clients Submitted Yet</h3>
              <p className="text-slate-500 mb-4">Click "New Client" to submit your first client for processing.</p>
              <button
                onClick={() => setShowNewClientModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <UserPlus className="w-4 h-4" />
                Submit New Client
              </button>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {submittedClients.map(client => (
                <div key={client.id} className="px-6 py-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-slate-900">{client.claimant_name}</h4>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3.5 h-3.5" />
                          {client.client_email}
                        </span>
                        {client.business_name && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3.5 h-3.5" />
                            {client.business_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                        client.status === 'submitted' ? 'bg-amber-100 text-amber-700' :
                        client.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                        client.status === 'completed' ? 'bg-green-100 text-green-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {client.status === 'submitted' && <Clock className="w-3 h-3" />}
                        {client.status === 'processing' && <Loader2 className="w-3 h-3 animate-spin" />}
                        {client.status === 'completed' && <CheckCircle2 className="w-3 h-3" />}
                        {client.status.charAt(0).toUpperCase() + client.status.slice(1)}
                      </span>
                      <p className="text-xs text-slate-400 mt-1">{formatDate(client.created_at)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Cases Tab - Portfolio & Cases List */}
      {activeTab === 'cases' && (
        <>
          {/* Portfolio */}
          <div className="mb-6">
            <CasePortfolio cases={cases} />
          </div>

          {/* Cases List */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Search and Filter Header */}
            <div className="px-6 py-4 border-b border-slate-200">
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Search */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by client, business, or event type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="case-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="px-4 py-2.5 border border-slate-200 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="case-sort-select"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="highest">Highest Demand</option>
              <option value="lowest">Lowest Demand</option>
              <option value="name">By Name</option>
            </select>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg transition-colors ${
                filterBy !== 'all' 
                  ? 'border-blue-500 bg-blue-50 text-blue-700' 
                  : 'border-slate-200 hover:bg-slate-50'
              }`}
            >
              <Filter className="w-4 h-4" />
              <span>Filter</span>
              {filterBy !== 'all' && (
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              )}
            </button>

            {/* Refresh */}
            <button
              onClick={fetchDashboard}
              className="flex items-center gap-2 px-4 py-2.5 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg transition-colors"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Filter Options */}
          {showFilters && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
              <span className="text-sm text-slate-500 mr-2">Event Type:</span>
              {[
                { value: 'all', label: 'All' },
                { value: 'fire', label: 'Fire' },
                { value: 'water', label: 'Water/Flood' },
                { value: 'storm', label: 'Storm/Hurricane' },
                { value: 'other', label: 'Other' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setFilterBy(option.value as FilterOption)}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                    filterBy === option.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}

          {/* Results count */}
          {(searchQuery || filterBy !== 'all') && (
            <p className="text-sm text-slate-500 mt-3">
              Showing {filteredCases.length} of {cases.length} cases
            </p>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="p-12 text-center">
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-slate-500">Loading cases...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="p-12 text-center">
            <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 font-medium">{error}</p>
            <button
              onClick={fetchDashboard}
              className="mt-4 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && cases.length === 0 && (
          <div className="p-12 text-center">
            <FileText className="w-14 h-14 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Released Cases</h3>
            <p className="text-slate-500 max-w-md mx-auto">
              When your cases are ready, they will appear here. Your ClaimSync administrator 
              will release reports once they are finalized.
            </p>
          </div>
        )}

        {/* No Results State */}
        {!loading && !error && cases.length > 0 && filteredCases.length === 0 && (
          <div className="p-12 text-center">
            <Search className="w-14 h-14 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Matching Cases</h3>
            <p className="text-slate-500 max-w-md mx-auto">
              No cases match your search or filter criteria. Try adjusting your filters.
            </p>
            <button
              onClick={() => { setSearchQuery(''); setFilterBy('all'); }}
              className="mt-4 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors"
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Cases Table with Timeline */}
        {!loading && !error && filteredCases.length > 0 && (
          <div className="divide-y divide-slate-100">
            {filteredCases.map((caseItem, index) => (
              <div
                key={caseItem.id}
                className="px-6 py-4 hover:bg-slate-50 transition-colors cursor-pointer group"
                onClick={() => navigate(`/attorney/case/${caseItem.id}`)}
                data-testid={`case-row-${caseItem.id}`}
              >
                <div className="flex items-center gap-4">
                  {/* Timeline indicator */}
                  <div className="hidden md:flex flex-col items-center">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    {index < filteredCases.length - 1 && (
                      <div className="w-0.5 h-8 bg-slate-200 mt-2"></div>
                    )}
                  </div>

                  {/* Case info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0 md:hidden">
                        <Scale className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-900 truncate">
                            {caseItem.client_name}
                          </p>
                          <span className="text-xs text-slate-400 hidden sm:inline">
                            {getRelativeTime(caseItem.released_at)}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          {caseItem.business_name && (
                            <span className="truncate">{caseItem.business_name}</span>
                          )}
                          {caseItem.event_type && (
                            <span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-medium">
                              {caseItem.event_type}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    {/* Total Demand */}
                    <div className="text-right hidden sm:block">
                      <p className="font-bold text-green-600 text-lg">
                        {formatCurrency(caseItem.total_demand)}
                      </p>
                      <p className="text-xs text-slate-500">Total Demand</p>
                    </div>

                    {/* Released Date */}
                    <div className="text-right hidden lg:block">
                      <div className="flex items-center gap-1 text-slate-600">
                        <Calendar className="w-4 h-4" />
                        <span className="text-sm">{formatDate(caseItem.released_at)}</span>
                      </div>
                      <p className="text-xs text-slate-400">Released</p>
                    </div>

                    {/* Arrow */}
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-600 transition-colors" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
        </>
      )}

      {/* New Client Modal */}
      {showNewClientModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between rounded-t-xl">
              <div className="flex items-center gap-3">
                <UserPlus className="w-6 h-6 text-white" />
                <h2 className="text-xl font-bold text-white">Submit New Client</h2>
              </div>
              <button
                onClick={() => setShowNewClientModal(false)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Success Message */}
            {submitSuccess && (
              <div className="mx-6 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                <p className="text-green-800">{submitSuccess}</p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <p className="text-red-800">{error}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmitNewClient} className="p-6 space-y-4">
              {/* Client Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Client Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newClientForm.client_name}
                  onChange={(e) => setNewClientForm({...newClientForm, client_name: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="John Smith"
                  required
                  data-testid="new-client-name"
                />
              </div>

              {/* Client Email */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Client Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="email"
                    value={newClientForm.client_email}
                    onChange={(e) => setNewClientForm({...newClientForm, client_email: e.target.value})}
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="client@email.com"
                    required
                    data-testid="new-client-email"
                  />
                </div>
              </div>

              {/* Client Phone */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Client Phone
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="tel"
                    value={newClientForm.client_phone}
                    onChange={(e) => setNewClientForm({...newClientForm, client_phone: e.target.value})}
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="(555) 123-4567"
                    data-testid="new-client-phone"
                  />
                </div>
              </div>

              {/* Business Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Business Name
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={newClientForm.business_name}
                    onChange={(e) => setNewClientForm({...newClientForm, business_name: e.target.value})}
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Acme Restaurant LLC"
                    data-testid="new-client-business"
                  />
                </div>
              </div>

              {/* Business Type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Business Type
                </label>
                <select
                  value={newClientForm.business_type}
                  onChange={(e) => setNewClientForm({...newClientForm, business_type: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  data-testid="new-client-business-type"
                >
                  <option value="">Select business type...</option>
                  <option value="Restaurant">Restaurant / Food Service</option>
                  <option value="Retail">Retail Store</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Healthcare">Healthcare / Medical</option>
                  <option value="Professional Services">Professional Services</option>
                  <option value="Construction">Construction</option>
                  <option value="Hospitality">Hotel / Hospitality</option>
                  <option value="Auto">Auto Dealership / Repair</option>
                  <option value="Agriculture">Agriculture / Farming</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Event Type & Date Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Event Type
                  </label>
                  <select
                    value={newClientForm.event_type}
                    onChange={(e) => setNewClientForm({...newClientForm, event_type: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    data-testid="new-client-event-type"
                  >
                    <option value="">Select...</option>
                    <option value="Fire">Fire</option>
                    <option value="Water Damage">Water Damage</option>
                    <option value="Hurricane">Hurricane</option>
                    <option value="Tornado">Tornado</option>
                    <option value="Flood">Flood</option>
                    <option value="Earthquake">Earthquake</option>
                    <option value="Business Interruption">Business Interruption</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Event Date
                  </label>
                  <div className="relative">
                    <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="date"
                      value={newClientForm.event_date}
                      onChange={(e) => setNewClientForm({...newClientForm, event_date: e.target.value})}
                      className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      data-testid="new-client-event-date"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={newClientForm.notes}
                  onChange={(e) => setNewClientForm({...newClientForm, notes: e.target.value})}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Any additional information about the case..."
                  data-testid="new-client-notes"
                />
              </div>

              {/* Send Email Toggle */}
              <div className="bg-blue-50 rounded-lg p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newClientForm.send_email}
                    onChange={(e) => setNewClientForm({...newClientForm, send_email: e.target.checked})}
                    className="w-5 h-5 mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    data-testid="send-email-checkbox"
                  />
                  <div>
                    <span className="font-medium text-slate-900">Send portal link to client</span>
                    <p className="text-sm text-slate-600 mt-0.5">
                      Client will receive an email with a secure link to upload their documents (bank statements, P&L, tax returns, etc.)
                    </p>
                  </div>
                </label>
              </div>

              {/* Submit Button */}
              <div className="flex gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowNewClientModal(false)}
                  className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !newClientForm.client_name || !newClientForm.client_email}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  data-testid="submit-new-client-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4" />
                      {newClientForm.send_email ? 'Submit & Send Email' : 'Submit Client'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AttorneyRoleLayout>
  );
}
